(function () {
  /**
   * Cart mutation handlers MUST be idempotent: compare the desired cart with
   * the actual cart, mutate only on a mismatch, and return true only when a
   * write completed. Once the cart is correct they must return false. This
   * makes any unexpected cart-update event converge without another refresh.
   */
  window.CartCoordinator = window.CartCoordinator || {
    handlers: {},
    running: false,
    suppressNextRun: false,

    register(name, fn) {
      this.handlers[name] = fn;
    },

    async run() {
      if (this.running) return false;

      this.running = true;
      let cartChanged = false;

      try {
        for (const [name, handler] of Object.entries(this.handlers)) {
          if (typeof handler !== 'function') {
            console.warn('[CartCoordinator] Skipping non-function handler:', name);
            continue;
          }

          this._handlerActive = true;
          try {
            const changed = await handler();
            if (changed) cartChanged = true;
          } catch (error) {
            console.error('[CartCoordinator] Handler error:', name, error);
          } finally {
            this._handlerActive = false;
          }
        }

        if (cartChanged) {
          await refreshDawnCart();
        } else {
          this.suppressNextRun = false;
        }
      } catch (error) {
        this.suppressNextRun = false;
        console.error('[CartCoordinator] Cart refresh error:', error);
      } finally {
        this._handlerActive = false;
        this.running = false;
      }

      return cartChanged;
    },
  };

  const coordinator = window.CartCoordinator;

  if (coordinator._initialized) {
    window.cartFetch = window.fetch;
    return;
  }
  coordinator._initialized = true;

  function getDawnSections() {
    const sections = {
      drawer: document.querySelector('cart-drawer')?.dataset.sectionId || null,
      icon: document.getElementById('cart-icon-bubble') ? 'cart-icon-bubble' : null,
      items: document.getElementById('main-cart-items')?.dataset.id || null,
      footer: document.getElementById('main-cart-footer')?.dataset.id || null,
    };

    sections.ids = [...new Set([sections.drawer, sections.icon, sections.items, sections.footer].filter(Boolean))];
    return sections;
  }

  function getSectionSource(sectionHtml, selector) {
    if (!sectionHtml) return null;
    return new DOMParser().parseFromString(sectionHtml, 'text/html').querySelector(selector);
  }

  function renderAuxiliarySections(cartData, sections) {
    if (!cartData?.sections) return;

    const cartIcon = document.getElementById('cart-icon-bubble');
    const iconSource = getSectionSource(cartData.sections[sections.icon], '.shopify-section');
    if (cartIcon && iconSource) cartIcon.innerHTML = iconSource.innerHTML;

    const cartFooter = document.getElementById('main-cart-footer');
    const footerTarget = cartFooter?.querySelector('.js-contents');
    const footerSource = getSectionSource(cartData.sections[sections.footer], '.js-contents');
    if (footerTarget && footerSource) footerTarget.innerHTML = footerSource.innerHTML;

    cartFooter?.classList.toggle('is-empty', cartData.item_count === 0);
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', cartData.item_count === 0);
  }

  async function refreshDawnCart() {
    const sections = getDawnSections();
    const cartUrl = window.routes?.cart_url || '/cart';
    const params = new URLSearchParams({
      sections: sections.ids.join(','),
      sections_url: window.location.pathname,
    });
    const response = await window.fetch(`${cartUrl}.js?${params.toString()}`, {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`Cart sections request failed (${response.status})`);

    const cartData = await response.json();

    // Dawn's cart-items subscribers re-render the drawer/cart rows. The icon
    // and main-cart footer are not covered by that subscriber, so update them
    // from the same bundled sections response before announcing completion.
    renderAuxiliarySections(cartData, sections);

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS?.cartUpdate) {
      coordinator.suppressNextRun = true;
      try {
        await publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'fsr-coordinator',
          cartData,
        });
      } finally {
        coordinator.suppressNextRun = false;
      }
    } else {
      coordinator.suppressNextRun = false;
    }

    document.dispatchEvent(new CustomEvent('fsr:rendered', { detail: { cartData } }));
  }

  function installCartUpdateBridge() {
    if (coordinator._bridgeInstalled) return true;
    if (
      typeof subscribe !== 'function' ||
      typeof PUB_SUB_EVENTS === 'undefined' ||
      !PUB_SUB_EVENTS?.cartUpdate
    ) {
      return false;
    }

    coordinator._bridgeInstalled = true;
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (payload) {
      if (payload?.source === 'fsr-coordinator') {
        coordinator.suppressNextRun = false;
        return;
      }

      if (coordinator.suppressNextRun) {
        coordinator.suppressNextRun = false;
        return;
      }

      // Let the publisher finish Dawn's own render first (notably
      // product-form renders after its publish promise resolves).
      window.setTimeout(function () {
        coordinator.run();
      }, 0);
    });

    return true;
  }

  function installFetchInterceptor() {
    if (coordinator._fetchInstalled || typeof window.fetch !== 'function') return;
    coordinator._fetchInstalled = true;

    const originalFetch = window.fetch;
    let queue = Promise.resolve();

    const getUrl = function (input) {
      return typeof input === 'string' ? input : input?.url || '';
    };
    const isCartApi = function (input) {
      return /\/cart(\.js|\/(?:add|change|update)\.js)/.test(getUrl(input));
    };
    const isCartMutation = function (input) {
      return /\/cart\/(?:add|change|update)\.js/.test(getUrl(input));
    };

    window.fetch = function (url, options) {
      if (!isCartApi(url)) {
        return originalFetch.apply(this, arguments);
      }

      if (coordinator._handlerActive && isCartMutation(url)) {
        coordinator.suppressNextRun = true;
      }

      const args = arguments;
      return new Promise(function (resolve, reject) {
        queue = queue.then(async function () {
          try {
            for (let attempt = 0; attempt < 3; attempt += 1) {
              const response = await originalFetch.apply(window, args);
              if (response.status === 429) {
                const delay = 500 * Math.pow(2, attempt);
                console.warn(
                  '[cartFetch] 429 on ' + url + ', retry ' + (attempt + 1) + '/3 in ' + delay + 'ms'
                );
                await new Promise(function (done) {
                  window.setTimeout(done, delay);
                });
                continue;
              }
              resolve(response);
              return;
            }

            resolve(await originalFetch.apply(window, args));
          } catch (error) {
            reject(error);
          }
        });
      });
    };

    window.cartFetch = window.fetch;
  }

  installFetchInterceptor();

  if (!installCartUpdateBridge() && document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', installCartUpdateBridge, { once: true });
  }

  const runOnLoad = function () {
    installCartUpdateBridge();
    coordinator.run();
  };

  if (document.readyState === 'complete') {
    window.setTimeout(runOnLoad, 0);
  } else {
    window.addEventListener('load', runOnLoad, { once: true });
  }
})();
